/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';

interface Args {}

export default class StandaloneSalesforceSetupInAppMessagingConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked developerName?: string;
  @tracked url?: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (this.salesforceConfig.isPendingSetup) {
      return;
    }

    let { deploymentId, hostname } = this.salesforceInAppMessagingConfig || {};
    this.developerName = deploymentId || undefined;
    this.url = hostname || undefined;
  }

  get salesforceConfig() {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceInAppMessagingConfig() {
    return this.finStandaloneService.salesforceInAppMessagingConfig;
  }

  get isConnected(): boolean {
    return this.salesforceInAppMessagingConfig?.isConnected ?? false;
  }

  get isFormValid(): boolean {
    return !!this.developerName && !!this.url;
  }

  get isRevokeRunning(): boolean {
    return taskFor(this.revoke).isRunning;
  }

  get isConnectRunning(): boolean {
    return taskFor(this.connect).isRunning;
  }

  @restartableTask
  *connect(): TaskGenerator<void> {
    try {
      Object.assign(this.finStandaloneService.salesforceInAppMessagingConfig!, {
        hostname: this.url,
        deploymentId: this.developerName,
        organizationId: this.finStandaloneService.salesforceConfig!.organisationId,
      });
      yield this.finStandaloneService.salesforceInAppMessagingConfig!.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.live-chat-connect.save-success'),
      );
    } catch (error) {
      this.finStandaloneService.salesforceInAppMessagingConfig!.rollbackAttributes();
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.live-chat-connect.save-error'),
      });
    }
  }

  @dropTask
  *revoke(): TaskGenerator<void> {
    try {
      yield this.salesforceInAppMessagingConfig?.revoke();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.live-chat-connect.revoke-success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('standalone.salesforce.setup.live-chat-connect.revoke-error'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::InAppMessaging::Connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
    'standalone/salesforce/setup/in-app-messaging/connect': typeof StandaloneSalesforceSetupInAppMessagingConnect;
  }
}
